'use client'

import { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { lookupRussian, type RuLookupResult } from '@/app/translate/actions'
import { saveUserWord, type SaveWordResult } from '@/app/review/my-words-actions'
import { PopoverArrow, htmlZoom, placePopover, type PopoverSide } from '@/components/popover-arrow'
import { SpeakButtons } from '@/components/speak-buttons'
import { useVoiceInput } from '@/lib/use-voice-input'

// «Как сказать?» — удержание плавающей кнопки (tutor-fab.tsx): держишь,
// говоришь русское слово, отпускаешь — карточка с турецким вариантом.
// Навык из «Суперобучения» (гл. VI): прямо посреди разговора найти нужное
// слово и продолжить. Окно репетитора не открывается — разговор под
// карточкой не сбивается. Если до нажатия был фокус в поле ввода (реплика в
// чате, ответ в упражнении), «Вставить» дописывает слово туда в позицию курсора.
// Inline-стили — телефон может держать устаревший CSS-бандл (PROJECT.md).

// Отступ от низа экрана, когда карточка прижата к низу: над кнопками навигации
// Android (viewport-fit=cover рисует страницу под ними, владелец 23.10.2026).
const SAFE_BOTTOM = 'calc(env(safe-area-inset-bottom, 0px) + 8px)'

// Низ экрана в единицах getBoundingClientRect карточки: пустой fixed-блок у низа
// с отступом safe-area, он под тем же zoom, что карточка.
function viewportBottom(): number {
  const probe = document.createElement('div')
  probe.style.cssText = 'position:fixed;left:0;bottom:env(safe-area-inset-bottom, 0px);width:0;height:0;visibility:hidden'
  document.body.appendChild(probe)
  const top = probe.getBoundingClientRect().top
  probe.remove()
  return top
}

export type WordLookupHandle = {
  // atEnd — поле подобрано без фокуса, слово пойдёт в конец текста.
  start: (target: HTMLInputElement | HTMLTextAreaElement | null, atEnd?: boolean) => void
  stop: () => void
}

type Phase =
  | { kind: 'idle' }
  | { kind: 'listening' }
  | { kind: 'loading'; query: string }
  | { kind: 'done'; result: Extract<RuLookupResult, { status: 'ok' }> }
  | { kind: 'error'; notice: string }

// Поле, в которое можно вставить найденное слово: текстовое, не только для чтения.
export function editableTarget(el: Element | null): HTMLInputElement | HTMLTextAreaElement | null {
  if (el instanceof HTMLTextAreaElement) return el.readOnly || el.disabled ? null : el
  if (el instanceof HTMLInputElement && ['text', 'search', ''].includes(el.type)) return el.readOnly || el.disabled ? null : el
  return null
}

// Куда вставлять, если ни одно поле не было в фокусе (28.09): сначала
// помеченная реплика страницы (data-tk-reply — ответ в разговоре с AI), иначе
// первое видимое текстовое поле вне окна репетитора (ответ в упражнении).
// Без этого «Вставить» появлялась только после тапа в поле, и на лендинге
// обещание «вставить в реплику» не выполнялось.
export function findReplyField(): HTMLInputElement | HTMLTextAreaElement | null {
  if (typeof document === 'undefined') return null
  const visible = (el: Element) => el.getClientRects().length > 0
  const marked = editableTarget(document.querySelector('[data-tk-reply]'))
  if (marked && visible(marked)) return marked
  for (const el of Array.from(document.querySelectorAll('textarea, input'))) {
    if (el.closest('[data-tutor-panel]')) continue
    const field = editableTarget(el)
    if (field && visible(field)) return field
  }
  return null
}

// Значение ставится через нативный setter + событие input — иначе React не
// узнает об изменении и контролируемое поле откатит текст при следующем рендере.
// atEnd — поле выбрано без фокуса (findReplyField): курсора в нём не было,
// слово дописывается в конец, а не в нулевую позицию.
function insertIntoField(el: HTMLInputElement | HTMLTextAreaElement, word: string, atEnd: boolean) {
  const start = atEnd ? el.value.length : (el.selectionStart ?? el.value.length)
  const end = atEnd ? start : (el.selectionEnd ?? start)
  const before = el.value.slice(0, start)
  const after = el.value.slice(end)
  const pre = before && !/\s$/.test(before) ? ' ' : ''
  const post = after && !/^\s/.test(after) ? ' ' : ''
  const proto = el instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype
  Object.getOwnPropertyDescriptor(proto, 'value')?.set?.call(el, before + pre + word + post + after)
  el.dispatchEvent(new Event('input', { bubbles: true }))
  const caret = (before + pre + word).length
  el.focus()
  el.setSelectionRange(caret, caret)
}

export const WordLookup = forwardRef<WordLookupHandle, { anchorRef: React.RefObject<HTMLElement | null> }>(function WordLookup(
  { anchorRef },
  ref,
) {
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' })
  const [target, setTarget] = useState<HTMLInputElement | HTMLTextAreaElement | null>(null)
  const [atEnd, setAtEnd] = useState(false)
  const voice = useVoiceInput('ru-RU', true)
  const textRef = useRef('')
  const seqRef = useRef(0)
  const cardRef = useRef<HTMLDivElement>(null)
  const wasListening = useRef(false)

  useImperativeHandle(ref, () => ({
    start(nextTarget, nextAtEnd = false) {
      seqRef.current++
      textRef.current = ''
      setTarget(nextTarget)
      setAtEnd(nextAtEnd)
      setPhase({ kind: 'listening' })
      voice.start((t) => {
        textRef.current = t
      }, 'ru-RU')
    },
    stop() {
      voice.stop()
    },
  }))

  // Запись закончилась (отпустили кнопку или браузер сам закрыл сессию) —
  // переводим распознанное.
  useEffect(() => {
    const ended = wasListening.current && !voice.isListening
    wasListening.current = voice.isListening
    if (!ended || phase.kind !== 'listening') return
    const query = textRef.current.trim()
    if (!query) {
      setPhase({ kind: 'error', notice: voice.error ?? 'Не расслышал - держите кнопку и скажите слово по-русски.' })
      return
    }
    const seq = seqRef.current
    setPhase({ kind: 'loading', query })
    lookupRussian(query)
      .then((r) => {
        if (seq !== seqRef.current) return
        setPhase(r.status === 'ok' ? { kind: 'done', result: r } : { kind: 'error', notice: r.notice })
      })
      .catch(() => {
        if (seq === seqRef.current) setPhase({ kind: 'error', notice: 'Не получилось перевести - попробуйте ещё раз.' })
      })
  }, [voice.isListening, voice.error, phase.kind])

  // Тап мимо карточки или Esc — закрыть. Нажатие на саму кнопку не
  // закрывает: оно может начинать новый поиск.
  const visible = phase.kind !== 'idle'
  useEffect(() => {
    if (!visible || phase.kind === 'listening') return
    function onDown(e: PointerEvent) {
      const t = e.target as Node
      if (cardRef.current?.contains(t) || anchorRef.current?.contains(t)) return
      close()
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') close()
    }
    document.addEventListener('pointerdown', onDown)
    window.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onDown)
      window.removeEventListener('keydown', onKey)
    }
  }, [visible, phase.kind, anchorRef])

  function close() {
    seqRef.current++
    setPhase({ kind: 'idle' })
  }

  function insert(word: string) {
    if (target && target.isConnected) insertIntoField(target, word, atEnd)
    close()
  }

  // Карточка встаёт у шара, над ним или под ним, и указывает на него уголком
  // (задача 107 из Telegram). Высота меняется (слушаю, ищу, варианты),
  // поэтому место пересчитывается по ResizeObserver.
  // Над шаром карточка держится низом (bottom), а не верхом: открывается
  // маленькой ("Слушаю"), потом в неё приходят варианты, и с top она росла
  // вниз, закрывала шар и уходила за нижний край (задача 121 из Telegram).
  // Выше экрана не растёт: maxHeight и своя прокрутка.
  const [pos, setPos] = useState<{
    left: number
    top?: number
    bottom?: number | string
    maxHeight: number
    scroll: boolean
    arrowX: number
    side: PopoverSide | null
  } | null>(null)
  useLayoutEffect(() => {
    if (!visible) return
    const card = cardRef.current
    if (!card) return
    function place() {
      const btn = anchorRef.current?.getBoundingClientRect()
      const el = cardRef.current
      const rect = el?.getBoundingClientRect()
      if (!btn || !btn.width || !el || !rect || !el.offsetWidth) return setPos(null)
      // Во сколько раз getBoundingClientRect больше CSS-пикселей, считаем по самой
      // карточке: Chrome на компьютере отдаёт экранные px (= zoom), Chrome на Android
      // уже CSS px (= 1). Деление на zoom там срабатывало дважды, карточка казалась
      // уже, чем есть, и уезжала за правый край в "Крупном" (владелец 15.10.2026).
      // placePopover сам делит прямоугольник шара на zoom, поэтому приводим его к
      // экранным px.
      const r = rect.width / el.offsetWidth
      const k = htmlZoom() / r
      const h = el.offsetHeight
      const x = placePopover({ left: btn.left * k, top: btn.top * k, bottom: btn.bottom * k, width: btn.width * k }, el.offsetWidth, h, 14)
      // Низ экрана в тех же единицах, что прямоугольники: меряем пустой
      // fixed-блок, он под тем же zoom, что карточка. Отступ safe-area: при
      // viewport-fit=cover Chrome на Android рисует страницу под кнопками
      // навигации, и карточка с bottom: 8 уходила под них (владелец 23.10.2026).
      const vb = viewportBottom() / r
      const btnTop = btn.top / r
      const btnBottom = btn.bottom / r
      const maxHeight = vb - 16
      // Прокрутка срезала бы уголок, он торчит за край: только для длинной карточки.
      const scroll = el.scrollHeight > maxHeight + 1
      if (btnBottom + 14 + h <= vb - 8) setPos({ left: x.left, top: btnBottom + 14, maxHeight, scroll, arrowX: x.arrowX, side: 'top' })
      else if (btnTop - 14 - h >= 8) setPos({ left: x.left, bottom: vb - btnTop + 14, maxHeight, scroll, arrowX: x.arrowX, side: 'bottom' })
      else setPos({ left: x.left, bottom: SAFE_BOTTOM, maxHeight, scroll, arrowX: x.arrowX, side: null })
    }
    place()
    const ro = new ResizeObserver(place)
    ro.observe(card)
    window.addEventListener('resize', place)
    // Шар после нажатия может переехать (после "Верно!" он встаёт у кнопки
    // шага внизу): карточка оставалась на старом месте и закрывала "Дальше"
    // (владелец 20.10.2026). Пока карточка открыта, сверяем место шара каждый кадр.
    let lastAt = ''
    let raf = requestAnimationFrame(function follow() {
      const b = anchorRef.current?.getBoundingClientRect()
      const at = b ? `${Math.round(b.left)},${Math.round(b.top)}` : ''
      if (at !== lastAt) {
        if (lastAt) place()
        lastAt = at
      }
      raf = requestAnimationFrame(follow)
    })
    return () => {
      ro.disconnect()
      window.removeEventListener('resize', place)
      cancelAnimationFrame(raf)
      setPos(null)
    }
  }, [visible, anchorRef])

  // Проверка по факту: карточка вышла за низ или верх экрана - прижать к низу.
  // Прямоугольник шара на телефоне бывает в других единицах, чем карточка (у шара
  // свой zoom 0,77 внутри "Крупного"), и место у шара считалось с ошибкой: карточка
  // с вариантами уезжала за нижний край (задачи 121 и 135 из Telegram). Карточку
  // и пустой fixed-блок у низа экрана браузер меряет одинаково, им можно верить.
  useLayoutEffect(() => {
    if (!pos?.side) return
    const rect = cardRef.current?.getBoundingClientRect()
    if (!rect) return
    if (rect.bottom > viewportBottom() + 1 || rect.top < -1) {
      setPos({ left: pos.left, bottom: SAFE_BOTTOM, maxHeight: pos.maxHeight, scroll: pos.scroll, arrowX: pos.arrowX, side: null })
    }
  }, [pos])

  if (!visible || typeof document === 'undefined') return null

  const canInsert = !!target && target.isConnected

  return createPortal(
    <div
      ref={cardRef}
      data-tutor-panel
      role="dialog"
      aria-label="Как сказать по-турецки"
      style={{
        position: 'fixed',
        // Пока шар не измерен - внизу по центру, как раньше.
        ...(pos
          ? { left: pos.left, top: pos.top, bottom: pos.bottom, maxHeight: pos.maxHeight, overflowY: pos.scroll ? ('auto' as const) : undefined }
          : { left: '50%', bottom: 'max(96px, calc(env(safe-area-inset-bottom) + 88px))', transform: 'translateX(-50%)' }),
        width: 'min(360px, calc(var(--tk-vw, 100vw) - 32px))',
        zIndex: 60,
        padding: '14px 16px',
        borderRadius: 18,
        border: '1px solid var(--tk-fire)',
        background: 'oklch(0.22 0.07 258)',
        boxShadow: '0 18px 40px -16px oklch(0 0 0 / 0.7)',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--tk-gold)' }}>Как сказать?</div>
        {phase.kind !== 'listening' && (
          <button type="button" className="tk-back-btn" aria-label="Закрыть" onClick={close}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
              <path d="M6 6l12 12M18 6 6 18" />
            </svg>
          </button>
        )}
      </div>

      {phase.kind === 'listening' && (
        <div style={{ fontSize: 16 }}>
          <span style={{ color: 'oklch(0.7 0.16 25)' }}>● </span>
          {voice.interimText || <span style={{ opacity: 0.75 }}>Скажите слово по-русски и отпустите кнопку</span>}
        </div>
      )}

      {phase.kind === 'loading' && (
        <div style={{ fontSize: 16 }}>
          &quot;{phase.query}&quot; <span style={{ opacity: 0.7 }}>- ищу...</span>
        </div>
      )}

      {phase.kind === 'error' && <div style={{ fontSize: 16, lineHeight: 1.4 }}>{phase.notice}</div>}

      {pos?.side && !pos.scroll && <PopoverArrow x={pos.arrowX} side={pos.side} border="var(--tk-fire)" fill="oklch(0.22 0.07 258)" />}

      {phase.kind === 'done' && (
        <>
          <div style={{ fontSize: 15, opacity: 0.75 }}>&quot;{phase.result.query}&quot;</div>
          <LookupRow turkish={phase.result.turkish} query={phase.result.query} main canInsert={canInsert} onInsert={insert} />
          {phase.result.alternatives.map((a) => (
            <LookupRow key={a.turkish} turkish={a.turkish} query={phase.result.query} note={a.noteRu} canInsert={canInsert} onInsert={insert} />
          ))}
          {phase.result.source === 'dictionary' && <div style={{ fontSize: 14, opacity: 0.7 }}>Это слово есть в курсе</div>}
        </>
      )}
    </div>,
    document.body,
  )
})

const SAVE_LABEL: Record<SaveWordResult['status'] | 'saving' | 'error', string> = {
  saving: 'Сохраняю...',
  saved: 'В моих словах',
  already: 'В моих словах',
  'in-course': 'Уже в повторении',
  full: 'Мои слова заполнены',
  error: 'Не сохранилось',
}

// «В мои слова» — в личную колоду повторения (user_words, /review).
// Перевод карточки — то, что ученик сказал по-русски, плюс пометка варианта.
function LookupRow({
  turkish,
  query,
  note,
  main,
  canInsert,
  onInsert,
}: {
  turkish: string
  query: string
  note?: string
  main?: boolean
  canInsert: boolean
  onInsert: (word: string) => void
}) {
  const [save, setSave] = useState<keyof typeof SAVE_LABEL | null>(null)
  function onSave() {
    setSave('saving')
    saveUserWord({ turkish, russian: query, note })
      .then((r) => setSave(r.status))
      .catch(() => setSave('error'))
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
      <div style={{ flex: '1 1 auto', minWidth: 0 }}>
        <span style={{ fontSize: main ? 22 : 17, fontWeight: main ? 700 : 600 }}>{turkish}</span>
        {note && <span style={{ fontSize: 15, opacity: 0.75 }}> - {note}</span>}
      </div>
      <SpeakButtons text={turkish} size={main ? 18 : 15} withSlow={false} record />
      {canInsert && (
        <button type="button" className="tk-pill tk-pill--sm" onClick={() => onInsert(turkish)}>
          Вставить
        </button>
      )}
      <button
        type="button"
        className="tk-pill tk-pill--sm"
        disabled={save !== null && save !== 'error'}
        aria-pressed={save === 'saved' || save === 'already' || save === 'in-course'}
        onClick={onSave}
      >
        {save ? SAVE_LABEL[save] : '+ В мои слова'}
      </button>
    </div>
  )
}
